import {useQueryClient} from '@tanstack/react-query';
import {createFileRoute} from '@tanstack/react-router';
import {useEffect, useState} from 'react';

import {AutoMlQueryKeys, useLatestSyntheticDefectPipelineQuery} from '~api/automl.queries';
import {pushRoute, replaceRoute, Route as RouteEnum} from '~utils/routeUtil';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {BaseLayout} from '~components/_layout/BaseLayout';
import {LayoutContent} from '~components/_layout/layoutComponents';
import {GenerateDataSuccess} from '~components/models/finetune/generate-data/GenerateDataSuccess';
import {ModelFooter} from '~components/models/ModelFooter';
import {ModelWorkflowStep} from '~components/models/ModelWorkflowSteps';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {ModelProvider} from 'src/contexts/ModelProvider';

export const Route = createFileRoute('/model/finetune/generate-data-success')({
  component: GenerateDataSuccessPage,
});

function GenerateDataSuccessPage() {
  return (
    <ModelProvider mode='model-pages'>
      <BaseLayout showBackButton customContentAndFooter>
        <GenerateDataSuccessContent />
      </BaseLayout>
    </ModelProvider>
  );
}

function GenerateDataSuccessContent() {
  const queryClient = useQueryClient();
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();
  const [hasFreshData, setHasFreshData] = useState(false);

  // Invalidate stale cache on mount so we get a fresh fetch.
  useEffect(() => {
    queryClient
      .invalidateQueries({queryKey: AutoMlQueryKeys.latestImageGenerationPipeline(projectId, currentModel?.id)})
      .then(() => setHasFreshData(true));
  }, [queryClient, projectId, currentModel?.id]);

  const generationPipelineQuery = useLatestSyntheticDefectPipelineQuery({
    modelId: currentModel?.id,
  });
  const generationPipeline = generationPipelineQuery.data;
  const generationJobStatus = generationPipeline?.statusData?.job?.status;
  const generationPipelineState = generationPipeline?.statusData?.pipeline?.status;
  const isGenerationComplete = generationJobStatus === 'success' || generationPipelineState === 'completed';
  const generationPipelineTag = generationPipeline?.syntheticDefectTag;

  useEffect(() => {
    if (!hasFreshData) return;
    if (isGenerationComplete) return;

    // Pipeline is not completed — redirect back to finetune.
    replaceRoute(RouteEnum.modelFinetune);
  }, [hasFreshData, isGenerationComplete, generationPipelineState]);

  const handleShowResults = () => {
    pushRoute(RouteEnum.modelSamples, {
      query: generationPipelineTag ? {tags: generationPipelineTag} : undefined,
    });
  };

  return (
    <>
      <LayoutContent sx={{justifyContent: 'center', alignItems: 'center'}}>
        <GenerateDataSuccess onShowResults={handleShowResults} />
      </LayoutContent>
      <ModelFooter activeStep={ModelWorkflowStep.finetune} />
    </>
  );
}
